import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import content from "../content/cookie.json"
import { useLanguage } from '../utils/LanguageContext';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false
  });
  
  const { language, setLanguage } = useLanguage();
  const t = content[language];

  useEffect(() => {
    const consent = localStorage.getItem('lexora-cookie-consent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const acceptAll = () => {
    const newPreferences = {
      essential: true,
      analytics: true,
      marketing: true
    };
    savePreferences(newPreferences);
  };

  const acceptEssential = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false
    });
  };

  const savePreferences = (prefs) => {
    localStorage.setItem('lexora-cookie-consent', JSON.stringify({
      preferences: prefs,
      timestamp: new Date().toISOString()
    }));
    
    // Set analytics cookies based on preferences
    if (prefs.analytics) {
      // Initialize analytics here when implemented
      console.log('Analytics cookies enabled');
    }
    
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleCustomSettings = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

    return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Backdrop */}
      {showSettings && (
        <div
          className="absolute inset-0 bg-black/50 pointer-events-auto"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Cookie Banner */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-auto">
        <div className="bg-white border-t border-gray-200 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-start space-x-4">
              <Cookie className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />

              <div className="flex-1">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
                  {t.cookieBanner.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {t.cookieBanner.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={acceptAll}
                    className="bg-gradient-to-r from-[#4A7CFF] to-[#3B82F6] hover:bg-[#011f4b] text-white px-6 py-2 rounded font-medium transition-colors"
                  >
                    {t.cookieBanner.acceptAll}
                  </button>

                  <button
                    onClick={acceptEssential}
                    className="bg-[rgba(74,124,255,0.6)] hover:bg-[#011f4b] text-white-800 hover:text-white px-6 py-2 rounded font-medium transition-colors"
                  >
                    {t.cookieBanner.acceptEssential}
                  </button>

                  <button
                    onClick={() => setShowSettings(true)}
                    className="flex items-center text-gray-600 hover:text-[#4A7CFF] px-4 py-2 font-medium transition-colors"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t.cookieBanner.customize}
                  </button>
                </div>
              </div>

              <button
                onClick={() => setShowBanner(false)}
                className="text-black hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#333]">
                  {t.cookieBanner.settingsTitle}
                </h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Essential Cookies */}
                <div className="flex items-start justify-between">
                  <div className="flex-1 mr-4">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {t.cookieBanner.settings.essential.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t.cookieBanner.settings.essential.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-6 bg-green-600 rounded-full relative">
                      <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 right-0.5"></div>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between">
                  <div className="flex-1 mr-4">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {t.cookieBanner.settings.analytics.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t.cookieBanner.settings.analytics.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() =>
                        setPreferences((prev) => ({
                          ...prev,
                          analytics: !prev.analytics,
                        }))
                      }
                      className={`w-12 h-6 rounded-full relative transition-colors ${
                        preferences.analytics ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${
                          preferences.analytics
                            ? 'translate-x-6'
                            : 'translate-x-0.5'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between">
                  <div className="flex-1 mr-4">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {t.cookieBanner.settings.marketing.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t.cookieBanner.settings.marketing.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() =>
                        setPreferences((prev) => ({
                          ...prev,
                          marketing: !prev.marketing,
                        }))
                      }
                      className={`w-12 h-6 rounded-full relative transition-colors ${
                        preferences.marketing ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${
                          preferences.marketing
                            ? 'translate-x-6'
                            : 'translate-x-0.5'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex gap-3">
                <button
                  onClick={handleCustomSettings}
                  className="flex-1 bg-[#1AA06A] hover:bg-[#011f4b] text-white px-4 py-2 rounded font-medium transition-colors"
                >
                  {t.cookieBanner.savePreferences}
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-gray-600 hover:text-[#1AA06A] font-medium transition-colors"
                >
                  {t.cookieBanner.cancel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsent;